import { Checkbox, Dropdown, Empty, Form, Input, Menu, Modal, notification, Spin } from "antd";
import { ExclamationCircleOutlined, MoreOutlined } from "@ant-design/icons";
import esES from "antd/lib/locale/es_ES";
import { deleteRole, getAllPermissions, getAllRoles, postRole, putRole, putRolePermissions } from "api/private";
import { CommonSearchForm, InternalPageLayout, Table } from "components";
import { usePermissions } from "hooks";
import { useEffect, useMemo, useState } from "react";
import { getResourceLabel, Permission, permissionActions } from "utils/permissions";

interface Role {
    id: string;
    name: string;
    label?: string | null;
    permissions?: Permission[];
}

const Roles = () => {
    const { can } = usePermissions();
    const canWrite = can("roles", "write");
    const canDelete = can("roles", "delete");
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissionsCatalog, setPermissionsCatalog] = useState<Permission[]>([]);
    const [search, setSearch] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);
    const [form] = Form.useForm();

    const loadRoles = async () => {
        setIsLoading(true);
        const response: any = await getAllRoles();
        if (response.success === "true") {
            setRoles(response.data || []);
        } else {
            notification.error({
                message: "Error",
                description: response.errors || "No se pudieron cargar los roles"
            });
        }
        setIsLoading(false);
    };

    const loadPermissionsCatalog = async () => {
        const response: any = await getAllPermissions();
        if (response.success === "true") {
            setPermissionsCatalog(response.data || []);
        } else {
            notification.error({
                message: "Error",
                description: response.errors || "No se pudieron cargar los permisos"
            });
        }
    };

    useEffect(() => {
        loadRoles();
        loadPermissionsCatalog();
    }, []);

    const permissionResources = useMemo(() => (
        Array.from(new Set(permissionsCatalog.map((permission) => permission.resource)))
            .sort((first, second) => getResourceLabel(first).localeCompare(getResourceLabel(second)))
    ), [permissionsCatalog]);

    const openAddModal = () => {
        setEditingRole(null);
        setSelectedPermissionIds([]);
        form.resetFields();
        setModalVisible(true);
    };

    const openEditModal = (role: Role) => {
        setEditingRole(role);
        setSelectedPermissionIds(
            (role.permissions || [])
                .map((permission) => permission.id)
                .filter((id): id is number => id != null)
        );
        form.setFieldsValue(role);
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setEditingRole(null);
        setSelectedPermissionIds([]);
        form.resetFields();
    };

    const handleDelete = (role: Role) => {
        Modal.confirm({
            title: "Eliminar rol",
            icon: <ExclamationCircleOutlined />,
            content: `¿Seguro que deseas eliminar el rol "${role.name}"? Esta acción no se puede deshacer.`,
            okText: "Eliminar",
            okButtonProps: { danger: true },
            cancelText: "Cancelar",
            onOk: async () => {
                const response: any = await deleteRole(role.id);
                if (response.success !== "true") {
                    notification.error({ message: "Error", description: response.errors || "No se pudo eliminar el rol" });
                    return;
                }
                notification.success({ message: "Rol eliminado" });
                await loadRoles();
            }
        });
    };

    const togglePermission = (permissionId: number, checked: boolean) => {
        setSelectedPermissionIds((currentIds) => checked
            ? [...currentIds.filter((id) => id !== permissionId), permissionId]
            : currentIds.filter((id) => id !== permissionId)
        );
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setIsSaving(true);
            const payload = {
                name: values.name.trim(),
                label: values.label?.trim() || null
            };
            const response: any = editingRole
                ? await putRole(editingRole.id, { role: { id: editingRole.id, ...payload } })
                : await postRole({ role: payload });

            if (response.success !== "true") {
                throw new Error(response.errors || "No se pudo guardar el rol");
            }

            const roleId = editingRole?.id || response.data?.id;
            if (roleId) {
                const permissionsResponse: any = await putRolePermissions(roleId, {
                    permission_ids: selectedPermissionIds
                });
                if (permissionsResponse.success !== "true") {
                    throw new Error(permissionsResponse.errors || "No se pudieron guardar los permisos del rol");
                }
            }

            notification.success({ message: "Rol guardado" });
            closeModal();
            await loadRoles();
        } catch (error: any) {
            if (error?.errorFields) return;
            notification.error({ message: "Error", description: error.message || "No se pudo guardar el rol" });
        } finally {
            setIsSaving(false);
        }
    };

    const filteredRoles = roles.filter((role) => (
        `${role.name} ${role.label || ""}`
            .toLowerCase()
            .includes(search.toLowerCase())
    ));

    const columns = [
        { title: "Nombre", dataIndex: "name", key: "name", sorter: (first: Role, second: Role) => first.name.localeCompare(second.name) },
        { title: "Descripción", dataIndex: "label", key: "label", render: (label: string) => label || "—" },
        {
            title: "Permisos",
            key: "permissions",
            render: (_value: unknown, role: Role) => role.permissions?.length || 0
        },
        {
            title: "Acciones",
            key: "actions",
            render: (_value: unknown, role: Role) => {
                const items = [
                    canWrite && {
                        key: "edit",
                        label: "Editar",
                        onClick: () => openEditModal(role)
                    },
                    canDelete && {
                        key: "delete",
                        label: "Eliminar",
                        danger: true,
                        onClick: () => handleDelete(role)
                    }
                ].filter(Boolean) as any[];

                if (items.length === 0) return null;

                return (
                    <Dropdown overlay={<Menu items={items} />} trigger={["click"]}>
                        <MoreOutlined />
                    </Dropdown>
                );
            }
        }
    ];

    const permissionColumns = [
        {
            title: "Recurso",
            dataIndex: "resource",
            key: "resource",
            render: (resource: string) => getResourceLabel(resource)
        },
        ...permissionActions.map((action) => ({
            title: action,
            key: action,
            render: (_value: unknown, row: { resource: string }) => {
                const permission = permissionsCatalog.find(
                    ({ resource, action: permissionAction }) => resource === row.resource && permissionAction === action
                );
                if (!permission || permission.id == null) return null;
                const permissionId = permission.id;
                return (
                    <Checkbox
                        checked={selectedPermissionIds.includes(permissionId)}
                        onChange={(event) => togglePermission(permissionId, event.target.checked)}
                    />
                );
            }
        }))
    ];

    return (
        <InternalPageLayout title="Roles">
            <Spin spinning={isLoading}>
                <CommonSearchForm
                    clickAdd={openAddModal}
                    search={search}
                    onSearch={({ target }: any) => setSearch(target.value)}
                    canAdd={canWrite}
                />
                <Table
                    localeProv={esES}
                    size="small"
                    rowKey="id"
                    columns={columns}
                    dataSource={filteredRoles}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay roles" /> }}
                    scroll={{ x: "auto" }}
                    pagination={{ defaultPageSize: 10 }}
                    loading={isLoading}
                />
            </Spin>
            <Modal
                title={editingRole ? "Editar rol" : "Agregar rol"}
                visible={modalVisible}
                onCancel={closeModal}
                onOk={handleSubmit}
                confirmLoading={isSaving}
                okText="Guardar"
                cancelText="Cancelar"
                width={720}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Nombre"
                        name="name"
                        rules={[{ required: true, message: "Ingresa un nombre" }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item label="Descripción" name="label">
                        <Input />
                    </Form.Item>
                    <Form.Item label="Permisos">
                        <Table
                            size="small"
                            rowKey="resource"
                            columns={permissionColumns}
                            dataSource={permissionResources.map((resource) => ({ resource }))}
                            pagination={false}
                            scroll={{ x: "auto", y: 280 }}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </InternalPageLayout>
    );
};

export default Roles;
